/**
 * Money helpers. Amounts are always integer minor units (paise) in storage and logic.
 * Parsing and formatting happen only here — never parseFloat an amount anywhere else.
 */

/** Largest accepted amount: 99,99,99,999.99 in a 2-decimal currency. */
export const MAX_AMOUNT_MINOR = 99_999_999_999;
const MAX_INTEGER_DIGITS = 9;

export const SUPPORTED_CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD', 'JPY'];

export function localeFor(currency: string): string {
  return currency === 'INR' ? 'en-IN' : 'en-US';
}

/** Number of minor-unit digits (2 for INR/USD, 0 for JPY). */
export function currencyDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

export function currencySymbol(currency: string): string {
  try {
    const part = new Intl.NumberFormat(localeFor(currency), { style: 'currency', currency })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? currency;
  } catch {
    return currency;
  }
}

/**
 * Parse user input ("1,00,000.50", "450", ".5") into minor units without floating point.
 * Returns null for anything invalid, zero, negative or too large.
 */
export function parseAmount(input: string, digits = 2): number | null {
  const cleaned = input.replace(/[,\s]/g, '');
  if (!/^\d*(\.\d*)?$/.test(cleaned) || cleaned === '' || cleaned === '.') return null;

  const [intPart = '', fracPart = ''] = cleaned.split('.');
  if (fracPart.length > digits) return null;
  if (intPart.replace(/^0+/, '').length > MAX_INTEGER_DIGITS) return null;

  const minor = Number(intPart || '0') * 10 ** digits + Number(fracPart.padEnd(digits, '0') || '0');
  if (!Number.isSafeInteger(minor) || minor <= 0 || minor > MAX_AMOUNT_MINOR) return null;
  return minor;
}

/** Minor units → plain input string for editing, e.g. 45050 → "450.5", 45000 → "450". */
export function minorToInput(minor: number, digits = 2): string {
  const factor = 10 ** digits;
  const int = Math.floor(minor / factor);
  const frac = minor % factor;
  if (frac === 0) return String(int);
  return `${int}.${String(frac).padStart(digits, '0').replace(/0+$/, '')}`;
}

export type FormatOptions = {
  /** 'auto' hides ".00" on whole amounts (default); 'always' keeps them; 'none' rounds to whole units (averages). */
  decimals?: 'auto' | 'always' | 'none';
  /** "₹1.2L" style for chart axes. */
  compact?: boolean;
};

export function formatMoney(minor: number, currency: string, options: FormatOptions = {}): string {
  const digits = currencyDigits(currency);
  const major = minor / 10 ** digits;
  const whole = minor % 10 ** digits === 0;
  const fractionDigits =
    options.decimals === 'none' ? 0 : options.decimals === 'always' || !whole ? digits : 0;

  try {
    return new Intl.NumberFormat(localeFor(currency), {
      style: 'currency',
      currency,
      notation: options.compact ? 'compact' : 'standard',
      minimumFractionDigits: options.compact ? 0 : fractionDigits,
      maximumFractionDigits: options.compact ? 1 : fractionDigits,
    }).format(major);
  } catch {
    return `${currency} ${major.toFixed(fractionDigits)}`;
  }
}

export type KeypadKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '.' | 'back';

/** Applies one keypad press to the raw amount string. Pure — the keypad's whole state machine. */
export function applyKeypadKey(current: string, key: KeypadKey, digits = 2): string {
  if (key === 'back') return current.slice(0, -1);

  if (key === '.') {
    if (digits === 0 || current.includes('.')) return current;
    return current === '' ? '0.' : `${current}.`;
  }

  const [intPart, fracPart] = current.split('.');
  if (fracPart !== undefined) {
    return fracPart.length >= digits ? current : current + key;
  }
  if (intPart === '0') return key; // no leading zeros
  if (intPart.length >= MAX_INTEGER_DIGITS) return current;
  return current + key;
}

/** Raw keypad string → grouped display, keeping a trailing dot/zeros while typing ("1,00,000.5"). */
export function formatAmountInput(raw: string, currency: string): string {
  if (raw === '') return '0';
  const [intPart, fracPart] = raw.split('.');
  const grouped = new Intl.NumberFormat(localeFor(currency), { maximumFractionDigits: 0 }).format(
    Number(intPart || '0'),
  );
  return fracPart === undefined ? grouped : `${grouped}.${fracPart}`;
}

/** Integer percentage of part in total, safe for total = 0. */
export function percentOf(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}
